import { useProviderSearchContext } from "context/ProviderSearchContext";
import {
  REDIRECT_REASON_NOT_AUTHORIZED,
  REDIRECT_REASON_PARAM,
  RETURN_PARAM,
  WIZARD_TYPE_ACTIVATION,
  WIZARD_TYPE_LOGIN,
} from "core/consts";
import { getProviderSearchFromPathname } from "core/model/providerSearch";
import { getReferrer } from "core/model/utils/urls";
import { GenericErrorPage } from "dsl/atoms/ErrorPages";
import RedirectRoot from "dsl/atoms/RedirectRoot";
import lazy from "dsl/atoms/retry";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import LogoutPage from "dsl/ecosystems/LogoutPage";
import { PrintableProviderSearchListing } from "dsl/ecosystems/ProviderSearchListingPage/PrintableProviderSearchListing";
import ProviderSearchOnboarding from "dsl/ecosystems/ProviderSearchOnboarding";
import { useProvidersearchNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import {
  ProviderSearchFiltersWrapper,
  ProviderSearchHome,
  ProviderSearchRootPage,
  useProviderSearchFilterContext,
} from "dsl/molecules/ProviderSearchAppWrapper";
import { USER_FILTER_TREATMENT_LOCATION } from "dsl/organisms/Filters/consts";
import { useEffect } from "react";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

const ProviderSearchDashboardPage = lazy(
  () => import("dsl/ecosystems/ProviderSearchDashboard"),
);
const ProviderSearchDetailsPage = lazy(
  () => import("dsl/ecosystems/ProviderSearchDetailsPage"),
);
const ProviderSearchRequestPage = lazy(
  () => import("dsl/ecosystems/ProviderSearchRequestPage"),
);
const ProviderSearchListingPage = lazy(
  () => import("dsl/ecosystems/ProviderSearchListingPage"),
);
const ProviderSearchLoginWizard = lazy(
  () => import("dsl/ecosystems/ProviderSearchLoginWizard"),
);
const ProviderSearchAccountPage = lazy(
  () => import("dsl/ecosystems/ProviderSearchAccountPage"),
);
const ProviderSearchGlossary = lazy(
  () => import("dsl/ecosystems/ProviderSearchGlossary"),
);

function AuthWrapper() {
  const { isLoggedIn, setLoginModal } = useProviderSearchContext();
  const { goToOnboardingFunnel } = useProvidersearchNavigationHandlers();
  const toast = useToast();
  const [search] = useSearchParams();
  const ref = getReferrer();

  useEffect(() => {
    if (isLoggedIn) return;

    const redirect = encodeURIComponent(
      `${location.pathname}?${search.toString()}`,
    );
    const searchString = `?${RETURN_PARAM}=${redirect}&${REDIRECT_REASON_PARAM}=${REDIRECT_REASON_NOT_AUTHORIZED}`;

    goToOnboardingFunnel(searchString);
  }, [isLoggedIn, ref, setLoginModal, toast, goToOnboardingFunnel]);

  if (!isLoggedIn) {
    return null;
  }

  return <Outlet />;
}

export const ZIPCODE_REDIRECT_PARAM = "return_url";

const hasZipcodeInParams = (search: URLSearchParams) =>
  search.get(USER_FILTER_TREATMENT_LOCATION);

function ZipcodeCondition() {
  const context = useProviderSearchFilterContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  useEffect(() => {
    if (!hasZipcodeInParams(search)) {
      const redirect = encodeURIComponent(location.pathname);
      setTimeout(() => {
        navigate(
          {
            pathname: ``,
            search: `${ZIPCODE_REDIRECT_PARAM}=${redirect}`,
          },
          { replace: true },
        );
      }, 0);
    }
  }, [context, location, navigate, search]);

  if (!hasZipcodeInParams(search)) {
    return null;
  }

  return <Outlet context={context} />;
}

export default function ProviderSearchRoutes() {
  const defaultProviderSearch = getProviderSearchFromPathname();

  return (
    <Routes>
      <Route element={<ProviderSearchRootPage />} path="/*">
        {/* Logout page */}
        <Route
          element={
            <LogoutPage
              navigateTo={`providersearch/${defaultProviderSearch}/listing`}
            />
          }
          path="logout"
        />

        {/* Error page */}
        <Route element={<GenericErrorPage />} path="error" />

        {/* Account login route */}
        <Route
          element={<ProviderSearchLoginWizard wizardType={WIZARD_TYPE_LOGIN} />}
          path="providersearch/loginwizard"
        />

        {/* Account activation route */}
        <Route
          element={
            <ProviderSearchLoginWizard wizardType={WIZARD_TYPE_ACTIVATION} />
          }
          path="providersearch/activationwizard"
        />

        <Route
          element={<ProviderSearchHome />}
          path="providersearch/:provider_search/*"
        >
          <Route
            element={<ProviderSearchGlossary />}
            path="glossary/:section"
          />
          <Route element={<ProviderSearchOnboarding />} index />

          {/* these routes will have the filter state forced into the search parameters */}
          <Route element={<ProviderSearchFiltersWrapper />}>
            {/* Routes wrapped in ZipcodeCondition require zipcode to be set in the search parameters */}
            <Route element={<ZipcodeCondition />}>
              {/* Listing/Landing Page */}
              <Route element={<ProviderSearchListingPage />} path="listing" />
              <Route
                element={<PrintableProviderSearchListing />}
                path="printable-listing"
              />

              {/* Consultant Detail Page */}
              <Route
                element={<ProviderSearchDetailsPage />}
                path="consultant/:consultant_id"
              />

              {/* Provider Detail Page */}
              <Route
                element={<ProviderSearchDetailsPage />}
                path="provider/:careprovider_id"
              />

              {/* Print Provider Detail Page */}
              <Route
                element={<ProviderSearchDetailsPage />}
                path="print-provider/:careprovider_id"
              />

              {/* Print Consultant Detail Page */}
              <Route
                element={<ProviderSearchDetailsPage />}
                path="print-consultant/:consultant_id"
              />
            </Route>
          </Route>

          {/* Authenticated routes */}
          <Route element={<AuthWrapper />}>
            {/* Dashboard Page */}
            <Route
              element={<ProviderSearchDashboardPage />}
              path="dashboard/patient/:patient_id"
            />
            {/* Provider Request Page */}
            <Route
              element={<ProviderSearchRequestPage />}
              path="request/:auction_request_id"
            />
            <Route
              element={<ProviderSearchAccountPage />}
              path="account/:account_id"
            />
          </Route>
          <Route element={<RedirectRoot to="listing" />} path="*" />
        </Route>
        <Route
          element={
            <RedirectRoot
              to={`providersearch/${defaultProviderSearch}/listing`}
            />
          }
          path="*"
        />
      </Route>
    </Routes>
  );
}
